import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  usePiece,
  usePieceAliases,
  usePieceArrangements,
  useArrangementGenres,
  useAddArrangementGenre,
  useRemoveArrangementGenre,
  useArrangementComments,
  useAddComment,
  useDeleteComment,
} from '@/lib/queries/pieces';
import {
  useArrangementResources,
  useAddResourceLink,
  useUploadResourceFile,
  useDeleteResource,
} from '@/lib/queries/resources';
import { useRepertoire } from '@/lib/queries/repertoire';
import { useGenres } from '@/lib/queries/reference-data';
import { Button } from '@/components/atoms/button';
import { Icon } from '@/components/atoms/icon';
import { CommentSection } from '@/components/comment-section';
import { GenreBadges } from '@/components/genre-badges';
import { ResourceSection } from '@/components/resource-section';
import { CreateArrangementDialog } from '@/components/create-arrangement-dialog';
import { ArrangementLine } from '@/components/molecules/arrangement-line';
import { AddToRepertoireSheet } from '@/components/organisms/add-to-repertoire-sheet';
import type { Arrangement } from '@/lib/types';

export const Route = createFileRoute('/library_/$pieceId')({
  component: PieceDetailPage,
});

type ArrangementExpandedBodyProps = {
  arrangement: Arrangement;
};

function ArrangementExpandedBody({ arrangement }: ArrangementExpandedBodyProps): ReactElement {
  const { data: genres } = useArrangementGenres(arrangement.arrangementId);
  const { data: comments } = useArrangementComments(arrangement.arrangementId);
  const { data: resources } = useArrangementResources(arrangement.arrangementId);
  const { data: genresData } = useGenres();

  const addGenre = useAddArrangementGenre();
  const removeGenre = useRemoveArrangementGenre();
  const addComment = useAddComment();
  const deleteComment = useDeleteComment();
  const addLink = useAddResourceLink();
  const uploadFile = useUploadResourceFile();
  const deleteResource = useDeleteResource();

  const allGenres = genresData?.content ?? [];

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">Genres</h4>
        <GenreBadges
          genres={genres ?? []}
          allGenres={allGenres}
          onAdd={(genreId) => addGenre.mutate({ arrangementId: arrangement.arrangementId, genreId })}
          onRemove={(genreId) =>
            removeGenre.mutate({ arrangementId: arrangement.arrangementId, genreId })
          }
        />
      </section>

      <section className="space-y-3">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">Notes</h4>
        <CommentSection
          comments={comments ?? []}
          onAdd={(content) =>
            addComment.mutate({ arrangementId: arrangement.arrangementId, content })
          }
          onDelete={(commentId) =>
            deleteComment.mutate({ arrangementId: arrangement.arrangementId, commentId })
          }
          error={addComment.error?.message ?? deleteComment.error?.message ?? null}
        />
      </section>

      <section className="space-y-3">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">Resources</h4>
        <ResourceSection
          resources={resources ?? []}
          onAddLink={(data) => void addLink.mutateAsync({ arrangementId: arrangement.arrangementId, ...data })}
          onUploadFile={(file, resourceType, title) =>
            void uploadFile.mutateAsync({ arrangementId: arrangement.arrangementId, file, resourceType, title })
          }
          onDelete={(resourceId) =>
            void deleteResource.mutateAsync({ arrangementId: arrangement.arrangementId, resourceId })
          }
          uploadStatus={{
            isPending: uploadFile.isPending,
            isError: uploadFile.isError,
            isSuccess: uploadFile.isSuccess,
            errorMessage: uploadFile.error?.message,
          }}
        />
      </section>
    </div>
  );
}

function PieceDetailPage(): ReactElement {
  const { pieceId } = Route.useParams();
  const { data: piece, isLoading: pieceLoading } = usePiece(pieceId);
  const { data: aliases } = usePieceAliases(pieceId);
  const { data: arrangementsData, isLoading: arrangementsLoading } = usePieceArrangements(pieceId);
  const { data: repertoireData } = useRepertoire({ size: 200 });

  const queryClient = useQueryClient();
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [isNewArrangementOpen, setIsNewArrangementOpen] = useState(false);
  const [sheetArrangement, setSheetArrangement] = useState<Arrangement | null>(null);

  const arrangements = arrangementsData?.content ?? [];

  const repertoireArrangementIds = useMemo(
    () => new Set((repertoireData?.content ?? []).map((r) => r.arrangementId)),
    [repertoireData],
  );

  const firstArrangement = arrangements[0] ?? null;

  if (pieceLoading) {
    return (
      <div className="min-h-screen bg-[var(--bg-page)] px-6 pt-8 lg:px-10 lg:pt-12">
        <div className="mx-auto max-w-4xl animate-pulse space-y-4">
          <div className="h-4 w-24 rounded bg-[var(--bg-soft)]" />
          <div className="h-10 w-1/2 rounded bg-[var(--bg-soft)]" />
          <div className="h-4 w-1/4 rounded bg-[var(--bg-soft)]" />
        </div>
      </div>
    );
  }

  if (!piece) {
    return (
      <div className="min-h-screen bg-[var(--bg-page)] px-6 pt-8 lg:px-10 lg:pt-12">
        <div className="mx-auto max-w-4xl space-y-4">
          <Link
            to="/library"
            className="inline-flex items-center gap-1 text-sm text-[var(--ink-3)] transition-colors hover:text-[var(--brand)]"
          >
            <Icon name="arr_l" size={16} />
            Back to library
          </Link>
          <p className="text-[var(--ink-3)]">Piece not found.</p>
        </div>
      </div>
    );
  }

  function handleArrangementCreated(arrangement: Arrangement): void {
    setIsNewArrangementOpen(false);
    setExpandedIds((prev) => ({ ...prev, [arrangement.arrangementId]: true }));
    void queryClient.invalidateQueries({ queryKey: ['pieces', pieceId, 'arrangements'] });
  }

  function toggleArrangement(arrangementId: string): void {
    setExpandedIds((prev) => ({ ...prev, [arrangementId]: !prev[arrangementId] }));
  }

  const arrangementsSection = (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-[18px] font-semibold">Arrangements</h2>
        <Button size="sm" onClick={() => setIsNewArrangementOpen(true)}>
          <Icon name="plus" size={16} /> New arrangement
        </Button>
      </div>

      {arrangementsLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl border border-[var(--rule)] bg-[var(--bg-surface)]" />
          ))}
        </div>
      )}

      {!arrangementsLoading && arrangements.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[var(--rule)] bg-[var(--bg-surface)] px-5 py-10 text-center">
          <p className="text-sm text-[var(--ink-3)]">No arrangements yet.</p>
          <div className="mt-4">
            <Button size="sm" onClick={() => setIsNewArrangementOpen(true)}>
              <Icon name="plus" size={16} /> New arrangement
            </Button>
          </div>
        </div>
      )}

      {!arrangementsLoading &&
        arrangements.map((arrangement) => (
          <ArrangementLine
            key={arrangement.arrangementId}
            name={arrangement.prettyName}
            musicalKey={arrangement.keyName ?? undefined}
            form={arrangement.formName ?? undefined}
            arranger={arrangement.arrangerName ?? undefined}
            inRepertoire={repertoireArrangementIds.has(arrangement.arrangementId)}
            expanded={Boolean(expandedIds[arrangement.arrangementId])}
            onToggle={() => toggleArrangement(arrangement.arrangementId)}
            onAdd={() => setSheetArrangement(arrangement)}
          >
            <ArrangementExpandedBody arrangement={arrangement} />
          </ArrangementLine>
        ))}
    </section>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--ink)]">
      {/* Single header — uses Tailwind responsive utilities so the same DOM nodes restyle across breakpoints */}
      <header className="flex items-center gap-3 border-b border-[var(--rule)] bg-[var(--bg-surface)] px-5 py-4 md:gap-4 md:px-8 md:py-5">
        <Link
          to="/library"
          className="flex-none text-[var(--ink-3)] transition-colors hover:text-[var(--brand)]"
          aria-label="Back to library"
        >
          <Icon name="arr_l" size={20} />
        </Link>
        <h1 className="font-display text-[28px] font-semibold leading-tight tracking-tight truncate md:flex-1 md:min-w-0">
          {piece.prettyName}
        </h1>
        {firstArrangement && !repertoireArrangementIds.has(firstArrangement.arrangementId) && (
          <Button
            variant="primary"
            size="sm"
            icon="plus"
            className="hidden md:inline-flex"
            onClick={() => setSheetArrangement(firstArrangement)}
          >
            Add to repertoire
          </Button>
        )}
      </header>

      <main className="px-5 pt-6 pb-32 md:px-8 md:pt-8 md:pb-10 md:grid md:grid-cols-[1fr_420px] md:gap-8 md:items-start">
        {/* Hero column (col 1 on tablet+, full-width above arrangements on mobile) */}
        <div className="space-y-6">
          {/* Piece metadata badges */}
          <div className="flex flex-wrap items-center gap-2">
            {piece.composerName && (
              <span className="rounded-full border border-[var(--rule)] bg-[var(--bg-soft)] px-3 py-1 text-xs font-medium text-[var(--ink)]">
                {piece.composerName}
              </span>
            )}
          </div>

          {/* Aliases */}
          {aliases && aliases.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">Also known as</span>
              {aliases.map((alias) => (
                <span
                  key={alias.pieceAliasId}
                  className="rounded-full border border-[var(--rule)] bg-[var(--bg-soft)] px-3 py-1 text-xs font-medium text-[var(--ink)]"
                >
                  {alias.name}
                </span>
              ))}
            </div>
          )}

          {/* Hero placeholder */}
          <div className="placeholder-stripe h-48 rounded-[var(--r-md)] border border-dashed border-[var(--rule)]" />

          {/* "Often paired with" placeholder */}
          <div className="space-y-2">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">Often paired with</h3>
            <div className="placeholder-stripe h-20 rounded-[var(--r-md)] border border-dashed border-[var(--rule)]" />
          </div>
        </div>

        {/* Arrangements column (col 2 on tablet+; below hero on mobile via mt-6 and grid order) */}
        <div className="mt-6 md:mt-0">{arrangementsSection}</div>
      </main>

      {/* Sticky mobile CTA */}
      {firstArrangement && !repertoireArrangementIds.has(firstArrangement.arrangementId) && (
        <div className="fixed bottom-0 inset-x-0 border-t border-[var(--rule)] bg-[var(--bg-surface)] px-5 py-4 md:hidden">
          <Button
            variant="primary"
            className="w-full"
            icon="plus"
            onClick={() => setSheetArrangement(firstArrangement)}
          >
            Add to repertoire
          </Button>
        </div>
      )}

      {isNewArrangementOpen && piece && (
        <CreateArrangementDialog
          pieceId={piece.pieceId}
          pieceName={piece.prettyName}
          onClose={() => setIsNewArrangementOpen(false)}
          onCreated={handleArrangementCreated}
        />
      )}

      {sheetArrangement && (
        <AddToRepertoireSheet
          open
          pieceName={piece.prettyName}
          arrangement={{
            arrangementId: sheetArrangement.arrangementId,
            prettyName: sheetArrangement.prettyName,
            keyName: sheetArrangement.keyName,
            formName: sheetArrangement.formName,
          }}
          onClose={() => setSheetArrangement(null)}
        />
      )}
    </div>
  );
}
